import React from "react";

/**
 * Shimmer placeholders shown while data is being fetched.
 * `Skeleton` is the base block; the rest are layouts that mirror real screens
 * so content doesn't jump when it arrives.
 */
const CARD =
  "bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)]";

export const Skeleton = ({ className = "" }) => (
  <div
    aria-hidden="true"
    className={`shimmer ${/\brounded/.test(className) ? "" : "rounded-md"} ${className}`}
  />
);

const Busy = ({ label, className = "", children }) => (
  <div role="status" aria-busy="true" aria-label={label} className={className}>
    {children}
  </div>
);

export const StatTilesSkeleton = ({ count = 4, className = "grid grid-cols-2 md:grid-cols-4 gap-3" }) => (
  <Busy label="Loading" className={className}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-4">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="mt-3 h-7 w-20" />
      </div>
    ))}
  </Busy>
);

export const TableSkeleton = ({ rows = 5, cols = 6 }) => (
  <Busy label="Loading" className="divide-y divide-gray-100">
    <div className="flex gap-6 bg-gray-50/80 px-5 py-3.5">
      {Array.from({ length: cols }).map((_, i) => (
        <Skeleton key={i} className="h-3 flex-1" />
      ))}
    </div>
    {Array.from({ length: rows }).map((_, r) => (
      <div key={r} className="flex items-center gap-6 px-5 py-4">
        {Array.from({ length: cols }).map((_, c) => (
          <div key={c} className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-4/5" />
            {c > 0 && c < cols - 1 && <Skeleton className="h-3 w-3/5" />}
          </div>
        ))}
      </div>
    ))}
  </Busy>
);

export const ListSkeleton = ({ rows = 3, className = "space-y-2.5" }) => (
  <Busy label="Loading" className={className}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-200/70 p-3.5">
        <Skeleton className="h-10 w-10 flex-shrink-0 rounded-xl" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3.5 w-2/5" />
          <Skeleton className="h-3 w-3/5" />
        </div>
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
    ))}
  </Busy>
);

export const CardGridSkeleton = ({ count = 3, className = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" }) => (
  <Busy label="Loading" className={className}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={`${CARD} overflow-hidden`}>
        <Skeleton className="h-44 w-full rounded-none" />
        <div className="space-y-3 p-5">
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-3 w-2/5" />
          <Skeleton className="h-3 w-full" />
          <div className="flex gap-2 pt-2">
            <Skeleton className="h-10 flex-1 rounded-xl" />
            <Skeleton className="h-10 flex-1 rounded-xl" />
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>
        </div>
      </div>
    ))}
  </Busy>
);

const PanelSkeleton = ({ lines = 3, className = "" }) => (
  <div className={`${CARD} p-5 sm:p-6 ${className}`}>
    <Skeleton className="h-4 w-32" />
    <div className="mt-5 space-y-3">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-xl" />
      ))}
    </div>
  </div>
);

/** Dashboard home: hero, stat cards, status row, two-column panels. */
export const OverviewSkeleton = () => (
  <Busy label="Loading dashboard" className="space-y-5 lg:space-y-6">
    <Skeleton className="h-36 w-full rounded-3xl" />
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className={`${CARD} p-5`}>
          <div className="flex items-start justify-between">
            <div className="space-y-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-14" />
            </div>
            <Skeleton className="h-11 w-11 rounded-xl" />
          </div>
          <Skeleton className="mt-4 h-3 w-20" />
        </div>
      ))}
    </div>
    <Skeleton className="h-[4.5rem] w-full rounded-2xl" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
      <div className="space-y-5 lg:col-span-2">
        <PanelSkeleton lines={1} />
        <PanelSkeleton lines={2} />
      </div>
      <PanelSkeleton lines={4} />
    </div>
    <div className={`${CARD} overflow-hidden`}>
      <TableSkeleton rows={3} cols={4} />
    </div>
  </Busy>
);

/** Section header card + grid of cards (services, AI tools). */
export const SectionSkeleton = ({ cards = 3 }) => (
  <Busy label="Loading" className="space-y-6">
    <div className={`${CARD} p-5 sm:p-6`}>
      <div className="flex items-center justify-between gap-4">
        <div className="space-y-2.5">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-3 w-56" />
        </div>
        <Skeleton className="h-11 w-40 rounded-xl" />
      </div>
      <Skeleton className="mt-5 h-11 w-full rounded-xl" />
    </div>
    <CardGridSkeleton count={cards} />
  </Busy>
);

/** Charts page: stat tiles, a wide chart and two smaller panels. */
export const AnalyticsSkeleton = () => (
  <Busy label="Loading analytics" className="space-y-5">
    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={`${CARD} p-4`}>
          <Skeleton className="h-9 w-9 rounded-xl" />
          <Skeleton className="mt-4 h-6 w-14" />
          <Skeleton className="mt-2 h-3 w-20" />
        </div>
      ))}
    </div>
    <div className={`${CARD} p-5`}>
      <Skeleton className="h-4 w-40" />
      <Skeleton className="mt-5 h-64 w-full rounded-xl" />
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <PanelSkeleton lines={3} />
      <PanelSkeleton lines={3} />
    </div>
  </Busy>
);

/** Whole-app placeholder: sidebar, top bar and the overview content. */
export const DashboardShellSkeleton = () => (
  <Busy label="Loading" className="min-h-screen bg-gradient-to-br from-purple-50/60 via-gray-50 to-pink-50/60">
    <aside className="hidden lg:block fixed inset-y-3 left-3 w-60 rounded-2xl border border-gray-200/70 bg-white p-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-9 w-9 rounded-xl" />
        <div className="space-y-2">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-2.5 w-20" />
        </div>
      </div>
      <div className="mt-8 space-y-3">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 px-1">
            <Skeleton className="h-5 w-5 rounded-md" />
            <Skeleton className={`h-3.5 ${i % 3 === 0 ? "w-24" : i % 3 === 1 ? "w-32" : "w-20"}`} />
          </div>
        ))}
      </div>
    </aside>
    <div className="lg:pl-[15.75rem]">
      <div className="flex h-[4.5rem] items-center justify-between border-b border-gray-200/60 px-5 sm:px-6 lg:px-8">
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
        <Skeleton className="h-7 w-20 rounded-full" />
      </div>
      <div className="p-5 sm:p-6 lg:p-8">
        <OverviewSkeleton />
      </div>
    </div>
  </Busy>
);

export default Skeleton;
